import { TrendingUp, Heart, ShoppingBag, MessageCircle, Sparkles, Star, Play } from 'lucide-react'
import { demoProducts } from '@/lib/copy/demo-products'

// Real lifestyle shots so the mock reads as genuine UGC, not coloured fills.
const SHOTS = Object.values(demoProducts).map((p) => p.image)

/**
 * A more elaborate dashboard / widget mockup using pure SVG + CSS
 * gradients. Two stacked panes (browser preview + analytics card)
 * to read as a real product surface.
 */
export function DashboardMockup() {
  const tiles = [
    { hue: 340, label: '@maya.knits', tag: 'Linen tee', price: '$48', kind: 'photo' },
    { hue: 258, label: '@dustin', tag: '', price: '', kind: 'video', play: true },
    { hue: 200, label: '@harper.fits', tag: 'Wide-leg', price: '$72', kind: 'photo' },
    { hue: 30, label: 'Trustpilot', tag: '5★ review', price: '', kind: 'review' },
    { hue: 320, label: '@studio.b', tag: 'New drop', price: '$94', kind: 'photo', wide: true },
    { hue: 160, label: '@coastal', tag: '', price: '', kind: 'video', play: true },
  ]

  return (
    <div className="relative">
      {/* Browser preview */}
      <div className="relative rounded-3xl border bg-card shadow-2xl shadow-primary/10">
        <div className="flex h-9 items-center gap-1.5 border-b px-4">
          <span className="h-2.5 w-2.5 rounded-full bg-red-400/80" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
          <span className="ml-3 truncate text-xs text-muted-foreground">store.example.com</span>
          <span className="ml-auto rounded-md border bg-background px-1.5 py-0.5 text-[10px] text-muted-foreground">UGC powered by Idukki</span>
        </div>
        <div className="grid grid-cols-3 gap-2 p-3">
          {tiles.map((t, i) => (
            <div
              key={i}
              className={`relative aspect-[3/4] overflow-hidden rounded-xl bg-muted ${t.wide ? 'col-span-2 aspect-[3/2]' : ''}`}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={SHOTS[i % SHOTS.length]} alt="" aria-hidden loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
              {(t.kind === 'video' || t.kind === 'review') && <span className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />}
              {t.kind === 'video' && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-black">
                    <Play className="ml-0.5 h-4 w-4 fill-current" />
                  </div>
                </div>
              )}
              {t.kind === 'review' && (
                <div className="absolute inset-0 flex flex-col justify-end p-3 text-white">
                  <div className="flex gap-0.5 text-xs"><Star className="h-3 w-3 fill-current" /><Star className="h-3 w-3 fill-current" /><Star className="h-3 w-3 fill-current" /><Star className="h-3 w-3 fill-current" /><Star className="h-3 w-3 fill-current" /></div>
                  <div className="mt-1 text-[10px] leading-tight">&ldquo;Best fitting shorts I&rsquo;ve ever owned,the linen is incredible&rdquo;</div>
                </div>
              )}
              {t.tag && (
                <div className="absolute right-2 top-2 rounded-full bg-white/95 px-2 py-1 text-[10px] font-medium text-black">
                  <ShoppingBag className="mr-1 inline h-3 w-3" />{t.tag} · {t.price}
                </div>
              )}
              <div className="absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-black/40 px-2 py-1 text-[10px] text-white">
                <Heart className="h-3 w-3" /> {t.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Floating: Conversion lift card */}
      <div className="absolute -bottom-8 -left-6 hidden w-72 rounded-2xl border bg-card p-4 shadow-xl shadow-primary/10 sm:block">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-emerald-500/10 text-emerald-500"><TrendingUp className="h-4 w-4" /></div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Variant B vs Control</div>
        </div>
        <div className="mt-3 flex items-baseline gap-2">
          <div className="font-display text-3xl font-semibold tracking-tight text-emerald-600 dark:text-emerald-400">+34.2%</div>
          <div className="text-xs text-muted-foreground">conversion</div>
        </div>
        <div className="mt-2 text-xs text-muted-foreground">p &lt; 0.01 · 9,412 sessions · winner declared</div>
        <svg viewBox="0 0 200 50" className="mt-3 w-full">
          <defs>
            <linearGradient id="chart-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(16 185 129)" stopOpacity="0.4" />
              <stop offset="100%" stopColor="rgb(16 185 129)" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d="M0,40 L20,38 L40,35 L60,32 L80,28 L100,30 L120,22 L140,18 L160,12 L180,8 L200,5 L200,50 L0,50 Z" fill="url(#chart-grad)" />
          <path d="M0,40 L20,38 L40,35 L60,32 L80,28 L100,30 L120,22 L140,18 L160,12 L180,8 L200,5" stroke="rgb(16 185 129)" strokeWidth="2" fill="none" />
        </svg>
      </div>

      {/* Floating: Rights approved card */}
      <div className="absolute -top-6 -right-4 hidden items-center gap-2 rounded-xl border bg-card px-3 py-2 text-sm shadow-lg shadow-primary/10 sm:flex">
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-violet-500/10 text-violet-500"><MessageCircle className="h-4 w-4" /></div>
        <div>
          <div className="font-medium">Rights approved</div>
          <div className="text-xs text-muted-foreground">Audit trail saved</div>
        </div>
      </div>

      {/* Floating: AI tagged badge */}
      <div className="absolute top-1/2 -right-3 hidden -translate-y-1/2 items-center gap-2 rounded-full border bg-card px-3 py-1.5 text-xs shadow-md md:flex">
        <Sparkles className="h-3 w-3 text-primary" />
        <span className="font-medium">AI-tagged 12 products</span>
      </div>
    </div>
  )
}
